export type TranslationEntries = Record<string, string>;
export type InterpolationVars = Record<string, string | number>;

// Replace every `{name}` token in `text` with `vars[name]`. Unknown tokens are
// left untouched so a partially-supplied call never throws or blanks the slot.
function interpolate(text: string, vars?: InterpolationVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

// The whole translation runtime. The key IS the English source string, so the
// fallback is simply the key itself — no catalog lookup at render time. Resolve
// the agency's override, else the English key, then interpolate. `entries` holds only the keys the operator has filled.
function translate(
  key: string,
  entries: TranslationEntries,
  vars?: InterpolationVars,
): string {
  const override = entries[key];
  const message = override && override.length > 0 ? override : key;
  return interpolate(message, vars);
}

// Bind `entries` once (per request, per agency) to get a `t(key, vars)` closure.
export function createTranslator(entries: TranslationEntries) {
  return (key: string, vars?: InterpolationVars): string =>
    translate(key, entries, vars);
}

// Plural counterpart of `t`, modelled on WordPress `_n($single, $plural, $n)`.
// Both English forms are independent catalog keys (each translatable on its
// own), so the operator overrides "1 spot left" and "{n} spots left"
// separately. Two-form only (singular when count === 1) — full CLDR N-form
// plural categories are out of scope. `vars` interpolates into the chosen form.
export function createPluralTranslator(entries: TranslationEntries) {
  return (
    single: string,
    plural: string,
    count: number,
    vars?: InterpolationVars,
  ): string => translate(count === 1 ? single : plural, entries, vars);
}

export type Translator = ReturnType<typeof createTranslator>;
export type PluralTranslator = ReturnType<typeof createPluralTranslator>;
